// #766: derive the customer's first collection date — the first Direct Debit
// — from a CONFIRMED payment selection's persisted schedule snapshot alone.
// See the plan (docs/plans/2026-08-05-first-collection-date-slice-a.md) for
// the full derivation, the B1/B6 evidence this reuses from #767, and D1-D14
// for every alternative considered and rejected.
//
// Home: `services/` already holds this feature's pure, transport-free
// derivation modules (`appliedDeposit.ts`, `collectionTargets.ts`,
// `collectionPeriods.ts`, `evenMonthlyTerm.ts`, `planTerm.ts`); this is domain
// logic over a `PaymentSelection`, so it belongs beside them. Imports no
// React, no `api.ts`, and no component. NO WALL CLOCK — everything is derived
// from the persisted selection, so it behaves identically on a live quote, a
// resumed quote, and a read-only completed one (D1).

import type { PaymentSelection } from '../types/schedule.types';
import { isDateKey, type DateKey } from '../utils/dateKey';
import { isRowZeroOnAnchor } from './scheduleAnchor';

/**
 * A condition that stopped this helper naming a first-collection date, or that
 * means the elemis representation it depends on may have drifted. Never
 * includes "this selection predates #767" — that is expected forever and is
 * reported with `anomaly: undefined`, exactly as `appliedDeposit.ts` does.
 */
export type FirstCollectionAnomaly =
    | 'anchor-unusable' // an anchor is recorded but is not a calendar date
    | 'anchor-mismatch' // deposit request, anchor recorded, row 0 is not on it
    | 'no-deposit-row-on-anchor' // NO deposit requested yet row 0 sits on the anchor (B1 canary)
    | 'row-date-implausible'; // the located row is missing, or its date is not a calendar date

export type FirstCollection =
    /** A definite calendar date, plus the date the preview was anchored to. */
    | { readonly kind: 'date'; readonly date: DateKey; readonly quotedOn: DateKey }
    /** The caller MUST render prose, never a date. */
    | { readonly kind: 'unknown'; readonly anomaly?: FirstCollectionAnomaly };

/**
 * Resolves the first collection date — the customer's first Direct Debit —
 * from a CONFIRMED payment selection alone. Pure: no wall clock, no React, no
 * transport, no logging — the caller (`QuoteSummaryCard`,
 * `buildQuoteSnapshot`) owns observability. `selection` must already have
 * passed `isValidPaymentSelection`.
 *
 * The step order below is load-bearing and must not be rearranged — it is
 * what produces the fail-closed table's two different codes for an empty
 * schedule (a no-deposit request lands on `row-date-implausible`, a deposit
 * request lands on `anchor-mismatch`).
 */
export const resolveFirstCollectionDate = (selection: PaymentSelection): FirstCollection => {
    const { params, snapshot, scheduleAnchorDate } = selection;

    // Step 2 — the anchor gate, applied BEFORE the deposit/no-deposit branch
    // so the "as quoted" qualifier is unconditional (D2): no usable anchor,
    // no date, for EITHER branch. `undefined` is reserved for the one state
    // that is expected forever — a selection confirmed before #767 — and
    // must never warn.
    //
    // This ternary LOOKS DEAD to TypeScript. It is not — do not "simplify"
    // it. `DateKey` is `string` (`dateKey.ts:2`) and `isDateKey` is declared
    // `value is DateKey`, so inside `if (!isDateKey(scheduleAnchorDate))`
    // TypeScript narrows the variable to `Exclude<string | undefined, string>`
    // = `undefined`, and the `'anchor-unusable'` arm reads as unreachable. At
    // RUNTIME it is reachable and load-bearing — `'2026-99-99'` is a `string`
    // that `isDateKey` rejects, which is the entire reason the guard is a
    // calendar check and not a type check. The plan's fail-closed table rows
    // 2 and 3 are exactly this distinction, and test cases 4 and 5 pin it.
    // (`appliedDeposit.ts:190-194` writes the same ternary without the
    // artefact only because it branches on the plain boolean `anchorOk`,
    // which does not narrow.) No type-aware ESLint rule is configured in
    // `www/eslint.config.js`, so nothing will flag a "simplification" here —
    // a human reviewer might, and this comment is the answer.
    if (!isDateKey(scheduleAnchorDate)) {
        return {
            kind: 'unknown',
            anomaly: scheduleAnchorDate === undefined ? undefined : 'anchor-unusable',
        };
    }

    // Step 3 — row-0 identity. Step 2 has already proven the `isDateKey`
    // conjunct; the redundancy inside `isRowZeroOnAnchor` is deliberate so
    // the helper is used identically by both consumers (S1).
    const rowZeroOnAnchor = isRowZeroOnAnchor(snapshot.schedule, scheduleAnchorDate);

    // Step 4 — index selection. `params` reaches this module as an OPAQUE,
    // UNVALIDATED document (`StoreQuoteRequest` rules
    // `payment_selection.params` as `array` with no sub-rules, deliberately,
    // so artemis never re-derives elemis's maths), so `hasDeposit === true`
    // must be an EXACT `=== true`, never a truthy test — mirrors
    // `appliedDeposit.ts:101`.
    let index: number;
    if (params.hasDeposit === true) {
        // Contract §8 B6: with a deposit, row 0 is the period-0 deposit row
        // and the first collection is row 1.
        if (!rowZeroOnAnchor) {
            return { kind: 'unknown', anomaly: 'anchor-mismatch' };
        }
        index = 1;
    } else {
        // The B1 canary (D11): no deposit was requested, yet row 0 sits on
        // the anchor — contract §8 B1 has been violated and row 0 is a
        // period-0 row, not instalment 1. Printing it would show a DEPOSIT
        // date as the first collection.
        if (rowZeroOnAnchor) {
            return { kind: 'unknown', anomaly: 'no-deposit-row-on-anchor' };
        }
        index = 0;
    }

    // Step 5 — row validity. `row.date` is checked with `isDateKey`, not
    // trusted, for the same reason as the anchor above: `ScheduleRow.date` is
    // only `string` at runtime (`schedule.types.ts:43`) and arrives off a
    // redux-persist rehydration.
    const row = snapshot.schedule[index];
    if (row === undefined || !isDateKey(row.date)) {
        return { kind: 'unknown', anomaly: 'row-date-implausible' };
    }

    // Step 6 — the default path: row `index` IS the first collection.
    return { kind: 'date', date: row.date, quotedOn: scheduleAnchorDate };
};
